import React from 'react';
import type { Maybe } from '@soundxyz/utils';
import type { CSS, CSSProperties } from '@components-shared/src/stitches';
import { styled } from '@components-shared/src/stitches';
import type { Pathname } from '../link/AccessibleLink';
import { AccessibleLink } from '../link/AccessibleLink';
import { NextFutureImage } from './NextFutureImage';
import blurImage from './blur-image';

export const SquareImage = ({
  src,
  alt,
  size,
  priority = false,
  borderRadius,
  unoptimized = false,
  loading,
}: {
  src: string;
  alt: string;
  size: number;
  priority?: boolean;
  borderRadius?: CSSProperties['borderRadius'];
  unoptimized?: boolean;
  loading?: 'eager' | 'lazy';
}) => (
  <NextFutureImage
    placeholder={size > 40 ? 'blur' : undefined}
    blurDataURL={size > 40 ? blurImage(size, size) : undefined}
    src={src}
    alt={alt}
    priority={priority}
    sizes={`${size}px`}
    width={size}
    height={size}
    style={{
      borderRadius: borderRadius,
      aspectRatio: '1/1',
      objectFit: 'cover',
    }}
    unoptimized={unoptimized}
    loading={loading}
  />
);

export type SharedImageWrapperProps = {
  disableLink?: boolean;
  hasShadow?: boolean;
  hasBorder?: boolean;
  borderRadius?: CSSProperties['borderRadius'];
  scaleOnHover?: boolean;
};

const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
  e.stopPropagation();
};

export const ImageWrapper = ({
  children,
  css = {},
  hasDarkBorder = false,
  ...props
}: {
  children: React.ReactNode;
  css?: CSS;
  hasDarkBorder?: boolean;
} & ({ href: Maybe<Pathname>; onClickEffect?: () => void } | { onClick: () => void }) &
  SharedImageWrapperProps) => {
  const {
    hasShadow = false,
    hasBorder = false,
    borderRadius,
    disableLink,
    scaleOnHover = false,
  } = props;

  const link = 'href' in props ? props.href : undefined;
  const onClickEffect = 'onClickEffect' in props ? props.onClickEffect : undefined;
  const hasLink = !(disableLink || !link);

  const image = (
    <ImageContainer
      className={
        scaleOnHover ? 'group-hover:[&_img]:scale-[1.2] group-hover:[&_svg]:scale-[1.2]' : undefined
      }
      boxShadow={hasShadow}
      border={hasBorder}
      scaleOnHover={scaleOnHover}
      hasDarkBorder={hasDarkBorder}
      css={{
        borderRadius: borderRadius,
        ...css,
      }}
      onClick={'onClick' in props ? props.onClick : undefined}
    >
      {children}
    </ImageContainer>
  );
  return hasLink ? (
    <AccessibleLink onClick={onClickEffect || handleLinkClick} pathnameUri={link}>
      {image}
    </AccessibleLink>
  ) : (
    image
  );
};

const ImageContainer = styled('div', {
  position: 'relative',
  overflow: 'hidden',
  outline: 'none',

  variants: {
    border: {
      true: {
        border: '1px solid $neutral200',
      },
    },
    hasDarkBorder: {
      true: {
        border: '1px solid $base800',
      },
    },
    boxShadow: {
      true: {
        boxShadow:
          '0px 5px 12px rgba(0, 0, 0, 0.15), inset 2px 2px 20px rgba(255, 255, 255, 0.5), 0px 0px 0px 2px rgba(0, 0, 0, 0.04)',
      },
    },

    scaleOnHover: {
      true: {
        // https://github.com/google/model-viewer/issues/662
        // Fixes the issue where border radius was not applying in Safari
        transform: 'translateZ(0)',
        'img, svg': {
          transition: 'transform 0.4s ease',
        },
        '&:hover': {
          'img, svg': {
            transform: 'scale(1.2)',
          },
        },
      },
    },
  },
});
